/**
 * Digital resources of the portal, with their loading, error and empty states.
 *
 * - "home": a section of the home page, left out when the library offers none;
 * - "page": the content of the dedicated page, which explains an empty offer.
 */

import { Link } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { DigitalResourceTiles } from "./DigitalResourceTiles";
import { usePortalDigitalResources } from "./useDigitalResources";

interface DigitalResourcesSectionProps {
  variant: "home" | "page";
}

export function DigitalResourcesSection({ variant }: DigitalResourcesSectionProps) {
  const { data: resources, isLoading, error } = usePortalDigitalResources();
  const isHome = variant === "home";

  if (isHome && !isLoading && (error || !resources?.length)) {
    return null;
  }

  let content;
  if (isLoading) {
    content = (
      <div
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
        aria-busy="true"
        aria-label="Chargement des ressources numériques"
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-28 animate-pulse rounded-xl bg-gray-100 motion-reduce:animate-none dark:bg-gray-800"
          />
        ))}
      </div>
    );
  } else if (error) {
    content = (
      <p
        role="alert"
        className="flex items-center gap-2 rounded-lg bg-red-50 p-4 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-300"
      >
        <AlertCircle className="h-4 w-4" aria-hidden />
        {error.message}
      </p>
    );
  } else if (!resources?.length) {
    content = (
      <p className="rounded-xl bg-gray-100 p-6 text-center text-gray-600 dark:bg-gray-800 dark:text-gray-300">
        La bibliothèque ne propose pas encore de ressources numériques en ligne.
      </p>
    );
  } else {
    content = <DigitalResourceTiles resources={resources} />;
  }

  if (!isHome) {
    return content;
  }

  return (
    <section aria-labelledby="digital-resources-title">
      <div className="flex items-center justify-between">
        <h2
          id="digital-resources-title"
          className="text-2xl font-semibold text-gray-900 dark:text-white"
        >
          Ressources numériques
        </h2>
        <Link
          to="/opac/ressources-numeriques"
          className="text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
        >
          Tout voir →
        </Link>
      </div>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
        Presse, cours, livres et films en ligne, inclus dans votre abonnement.
      </p>
      <div className="mt-6">{content}</div>
    </section>
  );
}
